/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import { TalerMerchantApi } from "@gnu-taler/taler-util";
import { useTranslationContext } from "@gnu-taler/web-util/browser";
import { h, VNode } from "preact";
import { StateUpdater, useState } from "preact/hooks";
import { Tooltip } from "../../../../components/Tooltip.js";

const TALER_SCREEN_ID = 63;

type Entity = TalerMerchantApi.TemplateEntry;

interface Props {
  templates: Entity[];
  onDelete: (e: Entity) => void;
  onSelect: (e: Entity) => void;
  onNewOrder: (e: Entity) => void;
  onQR: (e: Entity) => void;
  onCreate: () => void;
  onLoadMoreBefore?: () => void;
  onLoadMoreAfter?: () => void;
}

export function CardTable({
  templates,
  onCreate,
  onDelete,
  onSelect,
  onQR,
  onNewOrder,
  onLoadMoreAfter,
  onLoadMoreBefore,
}: Props): VNode {
  const [rowSelection, rowSelectionHandler] = useState<string[]>([]);

  const { i18n } = useTranslationContext();

  return (
    <div class="card has-table">
      <header class="card-header">
        <p class="card-header-title">
          <i class="icon mdi mdi-qrcode" />
          <i18n.Translate>Templates</i18n.Translate>
        </p>
        <div class="card-header-icon" aria-label="more options">
          <Tooltip text={i18n.str`Add new templates`}>
            <button
              class="button is-info"
              type="button"
              onClick={onCreate}
              accessKey="+"
            >
              <i class="icon mdi mdi-plus mdi-36px" />
            </button>
          </Tooltip>
        </div>
      </header>
      <div class="card-content">
        <div class=" has-pagination">
          <div class="table-wrapper has-mobile-cards">
            {templates.length > 0 ? (
              <Table
                instances={templates}
                onDelete={onDelete}
                onSelect={onSelect}
                onNewOrder={onNewOrder}
                onQR={onQR}
                rowSelection={rowSelection}
                rowSelectionHandler={rowSelectionHandler}
                onLoadMoreAfter={onLoadMoreAfter}
                onLoadMoreBefore={onLoadMoreBefore}
              />
            ) : (
              <EmptyTable />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
interface TableProps {
  rowSelection: string[];
  instances: Entity[];
  onDelete: (e: Entity) => void;
  onNewOrder: (e: Entity) => void;
  onQR: (e: Entity) => void;
  onSelect: (e: Entity) => void;
  rowSelectionHandler: StateUpdater<string[]>;
  onLoadMoreBefore?: () => void;
  onLoadMoreAfter?: () => void;
}

function Table({
  instances,
  onLoadMoreAfter,
  onDelete,
  onNewOrder,
  onQR,
  onSelect,
  onLoadMoreBefore,
}: TableProps): VNode {
  const { i18n } = useTranslationContext();
  return (
    <div class="">
      {onLoadMoreBefore && (
        <Tooltip text={i18n.str`Load more templates before the first one`}>
          <button
            type="button"
            class="button is-fullwidth"
            onClick={onLoadMoreBefore}
          >
            <i18n.Translate>Load first page</i18n.Translate>
          </button>
        </Tooltip>
      )}
      <table class="table is-fullwidth is-striped is-hoverable is-fullwidth">
        <thead>
          <tr>
            <th>
              <i18n.Translate>ID</i18n.Translate>
            </th>
            <th>
              <i18n.Translate>Description</i18n.Translate>
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {instances.map((i) => {
            return (
              <tr key={i.template_id}>
                <td
                  onClick={(): void => onSelect(i)}
                  style={{ cursor: "pointer" }}
                >
                  {i.template_id}
                </td>
                <td
                  onClick={(): void => onSelect(i)}
                  style={{ cursor: "pointer" }}
                >
                  {i.template_description}
                </td>
                <td class="is-actions-cell right-sticky">
                  <div class="buttons is-right">
                    <Tooltip
                      text={i18n.str`Delete selected templates from the database`}
                    >
                      <button
                        type="button"
                        class="button is-danger is-small"
                        onClick={() => onDelete(i)}
                      >
                        <i18n.Translate>Delete</i18n.Translate>
                      </button>
                    </Tooltip>
                    <Tooltip text={i18n.str`Use template to create new order`}>
                      <button
                        type="button"
                        class="button is-info is-small"
                        onClick={() => onNewOrder(i)}
                      >
                        <i18n.Translate>Test</i18n.Translate>
                      </button>
                    </Tooltip>
                    <Tooltip
                      text={i18n.str`Generate a QR code for the template.`}
                    >
                      <button
                        type="button"
                        class="button is-info is-small"
                        onClick={() => onQR(i)}
                      >
                        <i18n.Translate>Show QR</i18n.Translate>
                      </button>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {onLoadMoreAfter && (
        <Tooltip text={i18n.str`Load more templates after the last one`}>
          <button
            type="button"
            class="button is-fullwidth"
            onClick={onLoadMoreAfter}
          >
            <i18n.Translate>Load next page</i18n.Translate>
          </button>
        </Tooltip>
      )}
    </div>
  );
}

function EmptyTable(): VNode {
  const { i18n } = useTranslationContext();
  return (
    <div class="content has-text-grey has-text-centered">
      <p>
        <span class="icon is-large">
          <i class="icon mdi mdi-magnify mdi-48px" />
        </span>
      </p>
      <p>
        <i18n.Translate>
          There are no templates to list yet, add more by pressing the + sign
        </i18n.Translate>
      </p>
    </div>
  );
}
